import React, { useEffect, useState } from 'react';
import { Modal, Slide, CircularProgress } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useHistory } from 'react-router-dom';
import { isValidString } from '../../common/CommonUtilsMethods';
import { GetAllWorkTaskTemplatesForAgency } from '../trustvault/worktasks/WorkTaskTemplateAPI';
import { ListTrustVaultDocumentSubTypesAgencyUserAPICall } from '../trustvault/api/TrustVaultPhiDocumentsAgencyUserAPI';
import { TrustVaultDocumentSubTypeRespDTO } from '../trustvault/interfaces/TrustVaultPhiDocumentsAgencyUserInterfaces';
import {
  WorkTaskTemplateResponseDTO,
  WorkTaskFormSectionResponseDTO,
  WorkTaskQuestionResponseDTO,
  WorkTaskTemplatePurpose,
  formatWorkTaskTemplateCreatedDisplay,
  formatWorkTaskTemplateCreatorDisplay,
  formatWorkTaskTemplateUpdatedDisplay,
  getWorkTaskQuestionTypeLabel,
  getWorkTaskTemplatePurposeDisplayLabel,
  isFileUploadWorkTaskQuestionType,
  shouldDisplayWorkTaskTemplateLastUpdated,
  templateRequiresClientSignature,
  templateRequiresWorkerSignature,
} from '../trustvault/worktasks/WorkTaskTemplateInterfaces';
import { WORKY_WEB_APP_PATHS } from '../../WorkyWebAppPaths';
import { buildEditWorkTaskTemplatePath } from '../trustvault/worktasks/WorkTaskTemplatePaths';
import { buildEditWorkerOnboardingTemplatePath } from './AgencyTasksPageInterfaces';
import { WorkySelectComponent } from '../../shared-components/worky-select-component/WorkySelectComponent';
import './agencyactions.css';

enum WorkTaskTemplatesPurposeFilter {
  ALL = 'ALL',
  WORK_TASK = 'WORK_TASK',
  JOBBER_ONBOARDING = 'JOBBER_ONBOARDING',
}

interface WorkTaskTemplatesPurposeFilterOption {
  readonly label: string;
  readonly value: WorkTaskTemplatesPurposeFilter;
}

const WORK_TASK_TEMPLATES_PURPOSE_FILTER_OPTIONS: readonly WorkTaskTemplatesPurposeFilterOption[] = [
  { label: 'All purposes', value: WorkTaskTemplatesPurposeFilter.ALL },
  { label: 'Worker task', value: WorkTaskTemplatesPurposeFilter.WORK_TASK },
  { label: 'Worker onboarding', value: WorkTaskTemplatesPurposeFilter.JOBBER_ONBOARDING },
];

const resolveEditPathForWorkTaskTemplate = (template: WorkTaskTemplateResponseDTO): string => {
  if (
    template.templatePurpose === WorkTaskTemplatePurpose.JOBBER_ONBOARDING
    || template.templatePurpose === 'JOBBER_ONBOARDING'
  ) {
    return buildEditWorkerOnboardingTemplatePath(template.templateId);
  }
  return buildEditWorkTaskTemplatePath(template.templateId);
};

const doesTemplateMatchPurposeFilter = (
  template: WorkTaskTemplateResponseDTO,
  purposeFilter: WorkTaskTemplatesPurposeFilter
): boolean => {
  if (purposeFilter === WorkTaskTemplatesPurposeFilter.ALL) {
    return true;
  }
  if (purposeFilter === WorkTaskTemplatesPurposeFilter.JOBBER_ONBOARDING) {
    return (
      template.templatePurpose === WorkTaskTemplatePurpose.JOBBER_ONBOARDING
      || template.templatePurpose === 'JOBBER_ONBOARDING'
    );
  }
  return (
    template.templatePurpose !== WorkTaskTemplatePurpose.JOBBER_ONBOARDING
    && template.templatePurpose !== 'JOBBER_ONBOARDING'
    && template.templatePurpose !== WorkTaskTemplatePurpose.CARE_PLAN
    && template.templatePurpose !== 'CARE_PLAN'
  );
};

const doesTemplateMatchSearchQuery = (
  template: WorkTaskTemplateResponseDTO,
  searchQuery: string
): boolean => {
  if (!isValidString(searchQuery)) {
    return true;
  }
  const lowerCaseQuery: string = searchQuery.trim().toLowerCase();
  const templateName: string = (template.templateName ?? '').toLowerCase();
  const purposeLabel: string = getWorkTaskTemplatePurposeDisplayLabel(template.templatePurpose).toLowerCase();
  return templateName.includes(lowerCaseQuery) || purposeLabel.includes(lowerCaseQuery);
};

const filterWorkTaskTemplatesForList = (
  templates: readonly WorkTaskTemplateResponseDTO[],
  searchQuery: string,
  purposeFilter: WorkTaskTemplatesPurposeFilter
): readonly WorkTaskTemplateResponseDTO[] => {
  return templates.filter(
    (template: WorkTaskTemplateResponseDTO) =>
      doesTemplateMatchPurposeFilter(template, purposeFilter)
      && doesTemplateMatchSearchQuery(template, searchQuery)
  );
};

const resolveDocumentSubTypeLabel = (
  documentSubTypeUniqueIdentifier: string | null | undefined,
  documentSubTypes: readonly TrustVaultDocumentSubTypeRespDTO[]
): string | null => {
  if (!isValidString(documentSubTypeUniqueIdentifier)) {
    return null;
  }
  const matchingSubType: TrustVaultDocumentSubTypeRespDTO | undefined = documentSubTypes.find(
    (documentSubType: TrustVaultDocumentSubTypeRespDTO) =>
      documentSubType.subTypeUniqueIdentifier === documentSubTypeUniqueIdentifier
  );
  if (matchingSubType) {
    return matchingSubType.subTypeLabel;
  }
  return documentSubTypeUniqueIdentifier;
};

const WorkTaskTemplateSignatureBadges = ({
  template,
}: {
  readonly template: WorkTaskTemplateResponseDTO;
}): React.ReactElement | null => {
  const requiresWorkerSignature: boolean = templateRequiresWorkerSignature(template.requiredAttributes);
  const requiresClientSignature: boolean = templateRequiresClientSignature(template.requiredAttributes);

  if (!requiresWorkerSignature && !requiresClientSignature) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {requiresWorkerSignature && (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            background: '#dbeafe',
            color: '#1d4ed8',
          }}
        >
          Worker signature
        </span>
      )}
      {requiresClientSignature && (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            background: '#ede9fe',
            color: '#6d28d9',
          }}
        >
          Client signature
        </span>
      )}
    </div>
  );
};

const WorkTaskTemplateDetailsContent = ({
  template,
  isMobile,
  onEditTemplate,
}: {
  readonly template: WorkTaskTemplateResponseDTO;
  readonly isMobile: boolean;
  readonly onEditTemplate?: (template: WorkTaskTemplateResponseDTO) => void;
}): React.ReactElement => {
  const [documentSubTypes, setDocumentSubTypes] = useState<readonly TrustVaultDocumentSubTypeRespDTO[]>([]);
  const history = useHistory();

  useEffect(() => {
    const loadDocumentSubTypes = async (): Promise<void> => {
      try {
        const response = await ListTrustVaultDocumentSubTypesAgencyUserAPICall();
        if (response?.documentSubTypes && Array.isArray(response.documentSubTypes)) {
          setDocumentSubTypes(response.documentSubTypes);
        }
      } catch (error) {
        console.error('Error loading worker file types for task template view:', error);
      }
    };

    void loadDocumentSubTypes();
  }, []);

  const handleEditTemplate = (): void => {
    if (onEditTemplate) {
      onEditTemplate(template);
      return;
    }
    history.push(resolveEditPathForWorkTaskTemplate(template));
  };

  return (
    <div style={{ width: '100%', textAlign: 'left' }}>
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--dark-text)', margin: '0 0 8px 0' }}>
              {template.templateName}
            </h2>
            <div style={{ fontSize: '14px', color: '#666', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span>Purpose: {getWorkTaskTemplatePurposeDisplayLabel(template.templatePurpose)}</span>
              <span>Creator: {formatWorkTaskTemplateCreatorDisplay(template)}</span>
              <span>Version: {template.templateVersion}</span>
              <span>Created: {formatWorkTaskTemplateCreatedDisplay(template.metaData)}</span>
              {shouldDisplayWorkTaskTemplateLastUpdated(template) ? (
                <span>Last updated: {formatWorkTaskTemplateUpdatedDisplay(template.metaData)}</span>
              ) : null}
              <WorkTaskTemplateSignatureBadges template={template} />
            </div>
          </div>
          <button
            type="button"
            onClick={handleEditTemplate}
            style={{
              padding: '10px 16px',
              background: '#2c68bd',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Edit template
          </button>
        </div>
      </div>

      <div style={{ marginTop: '32px' }}>
        {template.sections && template.sections.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {template.sections.map((section: WorkTaskFormSectionResponseDTO) => (
              <div
                key={section.sectionId}
                className="tag-card"
                style={{ margin: 0, border: '2px solid var(--worky-colour)' }}
              >
                <div className="tag-card-header">
                  <h3 className="tag-name" style={{ fontSize: '18px', margin: 0 }}>
                    {section.sectionTitle}
                  </h3>
                </div>
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {section.questions.map((question: WorkTaskQuestionResponseDTO, questionIndex: number) => (
                    <div
                      key={question.questionId || questionIndex}
                      style={{
                        padding: '12px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dark-text)' }}>
                          {questionIndex + 1}. {question.question}
                          {question.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-colour)', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600 }}>Type: </span>
                        <span>{getWorkTaskQuestionTypeLabel(question.questionType)}</span>
                        <span style={{ marginLeft: '16px', fontWeight: 600 }}>Required: </span>
                        <span>{question.required ? 'Yes' : 'No'}</span>
                      </div>
                      {question.description && (
                        <div style={{ fontSize: '14px', color: 'var(--text-colour)', marginTop: '8px' }}>
                          <strong>Description:</strong> {question.description}
                        </div>
                      )}
                      {question.options && question.options.length > 0 && (
                        <div style={{ fontSize: '14px', color: 'var(--text-colour)', marginTop: '8px' }}>
                          <strong>Options:</strong> {question.options.join(', ')}
                        </div>
                      )}
                      {question.activityTypes && question.activityTypes.length > 0 && (
                        <div style={{ fontSize: '14px', color: 'var(--text-colour)', marginTop: '8px' }}>
                          <strong>Activities:</strong>{' '}
                          {question.activityTypes.map((activityType) => activityType.activityLabel).join(', ')}
                        </div>
                      )}
                      {question.tableColumns && question.tableColumns.length > 0 && (
                        <div style={{ fontSize: '14px', color: 'var(--text-colour)', marginTop: '8px' }}>
                          <strong>Table columns:</strong>{' '}
                          {question.tableColumns.map((tableColumn) => tableColumn.columnLabel).join(', ')}
                          <span style={{ marginLeft: '8px', color: '#666' }}>
                            (workers add rows at completion)
                          </span>
                        </div>
                      )}
                      {isValidString(question.staticText) && (
                        <div style={{ fontSize: '14px', color: 'var(--text-colour)', marginTop: '8px' }}>
                          <strong>Static text:</strong>
                          <div style={{ whiteSpace: 'pre-wrap', marginTop: '4px' }}>
                            {question.staticText}
                          </div>
                        </div>
                      )}
                      {isFileUploadWorkTaskQuestionType(question.questionType)
                        && isValidString(question.documentSubTypeUniqueIdentifier) && (
                        <div style={{ fontSize: '14px', color: 'var(--text-colour)', marginTop: '8px' }}>
                          <strong>Worker file type:</strong>{' '}
                          {resolveDocumentSubTypeLabel(
                            question.documentSubTypeUniqueIdentifier,
                            documentSubTypes
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '20px', color: '#666' }}>No sections found in this template.</div>
        )}
      </div>
    </div>
  );
};

const CrudWorkTaskTemplates = ({ isMobile }: { readonly isMobile: boolean }): React.ReactElement => {
  const [templateData, setTemplateData] = useState<readonly WorkTaskTemplateResponseDTO[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkTaskTemplateResponseDTO | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const history = useHistory();

  const fetchTemplateData = async (): Promise<void> => {
    try {
      const data: WorkTaskTemplateResponseDTO[] | null = await GetAllWorkTaskTemplatesForAgency();
      if (data && Array.isArray(data)) {
        setTemplateData(data);
      }
    } catch (error) {
      console.error('Error fetching task templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplateData();
  }, []);

  const filterTemplates = (query: string): readonly WorkTaskTemplateResponseDTO[] => {
    if (!query) {
      return templateData;
    }
    const lowerCaseQuery: string = query.toLowerCase();
    return templateData.filter((template: WorkTaskTemplateResponseDTO) => {
      return (
        template.templateName.toLowerCase().includes(lowerCaseQuery) ||
        template.agencyName.toLowerCase().includes(lowerCaseQuery)
      );
    });
  };

  const filteredTemplates: readonly WorkTaskTemplateResponseDTO[] = filterTemplates(searchQuery);

  const openTemplateDetails = (template: WorkTaskTemplateResponseDTO): void => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={30} style={{ color: '#2c68bd' }} />
      </div>
    );
  }

  return (
    <div className={isMobile ? 'general-info-holder-mobile-alt' : 'account-settings-crud-part-inner'}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="create-tag-btn"
          onClick={() => history.push(WORKY_WEB_APP_PATHS.agencyCreateWorkTaskTemplate)}
          style={{ padding: '8px 16px' }}
        >
          <AddRoundedIcon style={{ fontSize: '18px' }} />
          Create Task Template
        </button>
      </div>

      <div className="crud-search-bar">
        <input
          type="text"
          placeholder="Search task templates..."
          value={searchQuery}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)}
          className="workforce-search-input"
        />
      </div>

      {filteredTemplates.length === 0 ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
          No task templates found. Create one to get started.
        </div>
      ) : (
        filteredTemplates.map((template: WorkTaskTemplateResponseDTO) => (
          <div
            key={template.templateId}
            className="account-settings-crud-item"
            onClick={() => openTemplateDetails(template)}
          >
            <div className="account-settings-crud-item-workforce-profile-holder">
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EditNoteRoundedIcon style={{ fontSize: '24px', color: '#666' }} />
              </div>
            </div>
            <div className="account-settings-crud-item-workforce-jobber-holder">
              <div>{template.templateName}</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {getWorkTaskTemplatePurposeDisplayLabel(template.templatePurpose)}
              </div>
              <WorkTaskTemplateSignatureBadges template={template} />
            </div>
            <div className="account-settings-crud-item-workforce-tag-holder">
              <div style={{ fontSize: '12px', color: '#666' }}>
                {template.sections?.length ?? 0} section{(template.sections?.length ?? 0) === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        ))
      )}

      {selectedTemplate && isModalOpen && (
        <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <Slide direction={isMobile ? 'up' : 'left'} in={isModalOpen} mountOnEnter unmountOnExit>
            <div className={isMobile ? 'agency-billing-modal-mobile' : 'agency-billing-modal'}>
              {isMobile ? (
                <button onClick={() => setIsModalOpen(false)} className="timeclock-close-mobile" />
              ) : (
                <button onClick={() => setIsModalOpen(false)} className="agency-billing-close">
                  <CloseRoundedIcon />
                </button>
              )}
              <div
                className={
                  isMobile ? 'org-jobber-workforce-modal-general-info-mobile' : 'org-jobber-workforce-modal-general-info'
                }
              >
                <WorkTaskTemplateDetailsContent
                  template={selectedTemplate}
                  isMobile={isMobile}
                  onEditTemplate={(templateToEdit: WorkTaskTemplateResponseDTO) => {
                    setIsModalOpen(false);
                    history.push(resolveEditPathForWorkTaskTemplate(templateToEdit));
                  }}
                />
              </div>
            </div>
          </Slide>
        </Modal>
      )}
    </div>
  );
};

const WorkTaskTemplatesListForModal = ({
  isMobile,
  onSelectTemplate,
}: {
  readonly isMobile: boolean;
  readonly onSelectTemplate: (template: WorkTaskTemplateResponseDTO) => void;
}): React.ReactElement => {
  const [templates, setTemplates] = useState<readonly WorkTaskTemplateResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [purposeFilter, setPurposeFilter] = useState<WorkTaskTemplatesPurposeFilter>(
    WorkTaskTemplatesPurposeFilter.ALL
  );
  const history = useHistory();

  useEffect(() => {
    const fetchTemplates = async (): Promise<void> => {
      try {
        const data: WorkTaskTemplateResponseDTO[] | null = await GetAllWorkTaskTemplatesForAgency();
        if (data) {
          setTemplates(data);
        }
      } catch (error) {
        console.error('Error fetching task templates:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const filteredTemplates: readonly WorkTaskTemplateResponseDTO[] = filterWorkTaskTemplatesForList(
    templates,
    searchQuery,
    purposeFilter
  );
  const hasActiveFilters: boolean =
    isValidString(searchQuery) || purposeFilter !== WorkTaskTemplatesPurposeFilter.ALL;
  const selectedPurposeFilterOption: WorkTaskTemplatesPurposeFilterOption =
    WORK_TASK_TEMPLATES_PURPOSE_FILTER_OPTIONS.find(
      (option: WorkTaskTemplatesPurposeFilterOption) => option.value === purposeFilter
    ) ?? WORK_TASK_TEMPLATES_PURPOSE_FILTER_OPTIONS[0];

  const handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value);
  };

  const handlePurposeFilterChange = (
    next: readonly WorkTaskTemplatesPurposeFilterOption[]
  ): void => {
    const selectedOption: WorkTaskTemplatesPurposeFilterOption | undefined = next[0];
    setPurposeFilter(selectedOption?.value ?? WorkTaskTemplatesPurposeFilter.ALL);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={30} style={{ color: '#2c68bd' }} />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        <p style={{ color: '#6b7280', marginBottom: '16px' }}>
          Create your first task template to get started
        </p>
        <button
          type="button"
          onClick={() => history.push(WORKY_WEB_APP_PATHS.agencyCreateWorkTaskTemplate)}
          style={{
            padding: '10px 20px',
            background: '#2c68bd',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Create Template
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: '16px',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          background: '#f9fafb',
        }}
      >
        <div className="agency-actions-crud-search-bar" style={{ boxShadow: 'none', background: 'white' }}>
          <input
            type="text"
            placeholder="Search by template name or purpose..."
            value={searchQuery}
            onChange={handleSearchQueryChange}
            className="agency-actions-search-input"
          />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'minmax(220px, 280px)',
            gap: 12,
          }}
        >
          <WorkySelectComponent<WorkTaskTemplatesPurposeFilterOption>
            options={WORK_TASK_TEMPLATES_PURPOSE_FILTER_OPTIONS}
            value={[selectedPurposeFilterOption]}
            onChange={handlePurposeFilterChange}
            getLabel={(option: WorkTaskTemplatesPurposeFilterOption): string => option.label}
            getValue={(option: WorkTaskTemplatesPurposeFilterOption): string => option.value}
            placeholder="Filter by purpose"
            enableSearch={false}
            minSelections={1}
            maxSelections={1}
          />
        </div>
      </div>

      {filteredTemplates.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
          {hasActiveFilters
            ? 'No templates match your search or filters.'
            : 'No templates found.'}
        </div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', background: 'white' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: '13px', fontWeight: 600, color: '#6b7280', width: '36%' }}>
                  TEMPLATE NAME
                </th>
                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: '13px', fontWeight: 600, color: '#6b7280', width: '22%' }}>
                  PURPOSE
                </th>
                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: '13px', fontWeight: 600, color: '#6b7280', width: '16%' }}>
                  SECTIONS
                </th>
                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: '13px', fontWeight: 600, color: '#6b7280', width: '26%' }}>
                  CREATED AT
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template: WorkTaskTemplateResponseDTO) => (
                <tr
                  key={template.templateId}
                  onClick={() => onSelectTemplate(template)}
                  style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                >
                  <td style={{ padding: '16px 24px' }}>
                    <div>{template.templateName}</div>
                    <WorkTaskTemplateSignatureBadges template={template} />
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    {getWorkTaskTemplatePurposeDisplayLabel(template.templatePurpose)}
                  </td>
                  <td style={{ padding: '16px 24px' }}>{template.sections?.length ?? 0}</td>
                  <td style={{ padding: '16px 24px' }}>
                    {formatWorkTaskTemplateCreatedDisplay(template.metaData)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const WorkTaskTemplatesPanel = ({ isMobile }: { readonly isMobile: boolean }): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] = useState<WorkTaskTemplateResponseDTO | null>(null);
  const history = useHistory();

  const handleCreateTemplate = (): void => {
    history.push(WORKY_WEB_APP_PATHS.agencyCreateWorkTaskTemplate);
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        <div>
          <div style={{ margin: 0, fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#1f2937' }}>
            Task Templates
          </div>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
            Create, view, and edit worker task and worker onboarding templates
          </p>
        </div>
        {!selectedTemplate && (
          <button
            type="button"
            onClick={handleCreateTemplate}
            className="worky-actions-btn-v2"
            style={{ height: '40px', fontSize: '13.5px', paddingLeft: '15px', paddingRight: '15px' }}
          >
            <AddRoundedIcon style={{ fontSize: '18px' }} />
            Create Template
          </button>
        )}
      </div>

      {selectedTemplate ? (
        <div>
          <button
            type="button"
            onClick={() => setSelectedTemplate(null)}
            style={{
              marginBottom: '16px',
              background: 'none',
              border: 'none',
              color: '#2c68bd',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            ← Back to list
          </button>
          <WorkTaskTemplateDetailsContent
            template={selectedTemplate}
            isMobile={isMobile}
            onEditTemplate={(templateToEdit: WorkTaskTemplateResponseDTO) => {
              history.push(resolveEditPathForWorkTaskTemplate(templateToEdit));
            }}
          />
        </div>
      ) : (
        <WorkTaskTemplatesListForModal isMobile={isMobile} onSelectTemplate={setSelectedTemplate} />
      )}
    </div>
  );
};

const ViewWorkTaskTemplatesModal = ({
  isOpen,
  onClose,
  isMobile,
}: {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly isMobile: boolean;
}): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] = useState<WorkTaskTemplateResponseDTO | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplate(null);
    }
  }, [isOpen]);

  const handleCreateTemplate = (): void => {
    onClose();
    history.push(WORKY_WEB_APP_PATHS.agencyCreateWorkTaskTemplate);
  };

  const handleEditTemplate = (templateToEdit: WorkTaskTemplateResponseDTO): void => {
    onClose();
    history.push(resolveEditPathForWorkTaskTemplate(templateToEdit));
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <div
        className="wgc-modal2"
        style={{
          width: isMobile ? '90%' : '70%',
          minHeight: isMobile ? '85vh' : '88vh',
          maxHeight: '95vh',
          outline: 'none',
        }}
      >
        <div className="wgc-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ margin: 0, fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#1f2937' }}>
              Task Templates
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              Create, view, and edit worker task and worker onboarding templates
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {!selectedTemplate && (
              <button
                type="button"
                onClick={handleCreateTemplate}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: '#2c68bd',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <AddRoundedIcon style={{ fontSize: '18px' }} />
                Create Template
              </button>
            )}
            <button type="button" onClick={onClose} className="wgc-modal-close">
              ×
            </button>
          </div>
        </div>
        <div
          className="wgc-modal-body"
          style={{
            overflowY: 'auto',
            minHeight: isMobile ? 'calc(85vh - 120px)' : 'calc(88vh - 120px)',
            maxHeight: 'calc(95vh - 120px)',
          }}
        >
          {selectedTemplate ? (
            <div>
              <button
                type="button"
                onClick={() => setSelectedTemplate(null)}
                style={{
                  marginBottom: '16px',
                  background: 'none',
                  border: 'none',
                  color: '#2c68bd',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                ← Back to list
              </button>
              <WorkTaskTemplateDetailsContent
                template={selectedTemplate}
                isMobile={isMobile}
                onEditTemplate={handleEditTemplate}
              />
            </div>
          ) : (
            <WorkTaskTemplatesListForModal isMobile={isMobile} onSelectTemplate={setSelectedTemplate} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export { CrudWorkTaskTemplates, ViewWorkTaskTemplatesModal, WorkTaskTemplatesPanel, WorkTaskTemplateDetailsContent };
